import type { Version } from '../api/client';

interface VersionBarProps {
  versions: Version[];
  activeVersion: number;
  latestVersion: number;
  onVersionChange: (version: number) => void;
}

export default function VersionBar({
  versions,
  activeVersion,
  latestVersion,
  onVersionChange,
}: VersionBarProps) {
  const isViewingLatest = activeVersion === latestVersion;

  return (
    <div
      role="tablist"
      aria-label="Version selector"
      className="flex items-center gap-[8px] px-[12px] py-[8px] border-t overflow-x-auto rounded-[8px]"
      style={{
        background: 'var(--rd-surface-hover)',
        borderColor: 'var(--rd-border)',
      }}
    >
      <div className="flex items-center gap-[4px] flex-shrink-0">
        {versions.map((v) => {
          const isActive = v.version_number === activeVersion;
          return (
            <button
              key={v.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => onVersionChange(v.version_number)}
              className="h-[28px] px-[12px] rounded-[100px] text-[13px] cursor-pointer transition-all duration-[150ms] border-0 whitespace-nowrap"
              style={{
                background: isActive ? 'var(--rd-surface-panel)' : 'transparent',
                color: isActive ? 'var(--rd-text-strong)' : 'var(--rd-text-meta)',
                fontWeight: isActive ? 600 : 400,
                boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              }}
              onMouseEnter={(e) => {
                if (!isActive)
                  e.currentTarget.style.background = 'rgba(0,0,0,0.05)';
              }}
              onMouseLeave={(e) => {
                if (!isActive)
                  e.currentTarget.style.background = 'transparent';
              }}
            >
              v{v.version_number}
            </button>
          );
        })}
      </div>

      <span
        className="ml-auto text-[13px] flex-shrink-0"
        style={{ color: 'var(--rd-text-faint)' }}
      >
        {isViewingLatest ? 'Viewing latest' : `Viewing v${activeVersion}`}
      </span>
    </div>
  );
}
